import { gql } from "@apollo/client";
import { Box, Stack, Typography } from "@mui/material";
import { coordinateToMapPoint, MapMarkerInput } from "@caredna-marlin/map-wrapper";
import { SOS_ACTIVE_WINDOW_HOURS } from "@caredna-marlin/utils/constants";
import { datef, timezoneDisplayLabel } from "@caredna-marlin/utils/dates";
import { addHours } from "date-fns";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import VisitStatusChip from "~/components/command/VisitStatusChip";
import {
  SosAlertDetailFragment,
  SosAlertEventEventTypeEnum,
  SosAlertStatusEnum,
} from "~/graphql/__generated__/graphql";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import RouteIndexView from "~/views/route";
import type {
  RouteLocalCalendarBlock,
  RouteLocalVisitResource,
  RouteProviderContext,
} from "~/views/route/types";

const EMPTY_VISIT_RESOURCES: RouteLocalVisitResource[] = [];
const EMPTY_CALENDAR_BLOCKS: RouteLocalCalendarBlock[] = [];
const NULL_PROVIDER_CONTEXT: RouteProviderContext = { addressCoordinates: null };

gql`
  fragment SosAlertDetail on SosAlertSelectItem {
    id
    status
    providerStatus
    latitude
    longitude
    addressStreet
    addressCity
    addressState
    addressTimezone
    createdAt
    provider {
      id
      firstName
      lastName
    }
    visit {
      addressStreet
      addressCity
      addressState
      addressTimezone
      patient {
        id
        firstName
        lastName
      }
    }
    events {
      id
      eventType
      note
      createdAt
      createdByUser {
        firstName
        lastName
      }
    }
  }
`;

export type SosAlert = SosAlertDetailFragment;
type SosAlertEvent = SosAlert["events"][number];

export const EVENT_LABEL: Record<SosAlertEventEventTypeEnum, string> = {
  [SosAlertEventEventTypeEnum.Created]: "SOS triggered",
  [SosAlertEventEventTypeEnum.Acknowledged]: "Acknowledged",
  [SosAlertEventEventTypeEnum.Resolved]: "Resolved",
  [SosAlertEventEventTypeEnum.Canceled]: "Canceled",
  [SosAlertEventEventTypeEnum.NoteAdded]: "Note added",
};

const RESOLUTION_EVENT_TYPES: SosAlertEventEventTypeEnum[] = [
  SosAlertEventEventTypeEnum.Resolved,
  SosAlertEventEventTypeEnum.Canceled,
];

export const patientName = (alert: SosAlert) => {
  const patient = alert.visit?.patient;
  if (!patient) {
    return null;
  }
  return [patient.firstName, patient.lastName].filter(Boolean).join(" ") || null;
};

export const eventActor = (event: SosAlertEvent) =>
  [event.createdByUser?.firstName, event.createdByUser?.lastName]
    .filter(Boolean)
    .join(" ") || "System";

export const providerName = (alert: SosAlert) =>
  [alert.provider?.firstName, alert.provider?.lastName].filter(Boolean).join(" ") ||
  "Unknown provider";

export const visitAddress = (alert: SosAlert) => {
  const visit = alert.visit;
  if (!visit) {
    return null;
  }
  const cityState = [visit.addressCity, visit.addressState].filter(Boolean).join(", ");
  return [visit.addressStreet, cityState].filter(Boolean).join(", ") || null;
};

export const sosAlertAddress = (alert: SosAlert) => {
  const cityState = [alert.addressCity, alert.addressState].filter(Boolean).join(", ");
  return [alert.addressStreet, cityState].filter(Boolean).join(", ") || null;
};

export const statusColor = (
  status: SosAlertStatusEnum,
): "error" | "warning" | "default" | "primary" => {
  switch (status) {
    case SosAlertStatusEnum.Active:
      return "error";
    case SosAlertStatusEnum.Acknowledged:
      return "warning";
    case SosAlertStatusEnum.Resolved:
      return "primary";
    case SosAlertStatusEnum.Canceled:
      return "default";
  }
};

export const SOS_STATUS_LABEL: Record<SosAlertStatusEnum, string> = {
  [SosAlertStatusEnum.Active]: "Active",
  [SosAlertStatusEnum.Acknowledged]: "Acknowledged",
  [SosAlertStatusEnum.Resolved]: "Resolved",
  [SosAlertStatusEnum.Canceled]: "Canceled",
};

export const sosAlertResolvedAt = (alert: SosAlert): Date | null => {
  const [resolution] = alert.events
    .filter((event) => RESOLUTION_EVENT_TYPES.includes(event.eventType))
    .map((event) => new Date(event.createdAt))
    .sort((a, b) => b.getTime() - a.getTime());
  return resolution ?? null;
};

export const sosAlertRouteRange = (alert: SosAlert) => {
  const createdAt = new Date(alert.createdAt);
  const maxRangeEnd = addHours(createdAt, SOS_ACTIVE_WINDOW_HOURS);
  const resolvedAt = sosAlertResolvedAt(alert);
  return {
    rangeStart: createdAt,
    rangeEnd: resolvedAt && resolvedAt < maxRangeEnd ? resolvedAt : maxRangeEnd,
  };
};

// Shared SOS timestamp formatter. Uses the viewer's timezone when the alert's
// visit has none, and a zone label that never degrades to a bare GMT offset.
export const useSosTimestampFormat = () => {
  const { timeLabel, timezone: viewerTimezone } = useZonedDateFormat();
  return (value: string | Date, timezone?: string | null) => {
    const tz = timezone ?? viewerTimezone ?? undefined;
    const zoneLabel = tz ? ` ${timezoneDisplayLabel(tz)}` : "";
    return `${datef(value, "MMM d, yyyy", tz)} · ${timeLabel(value, true, tz)}${zoneLabel}`;
  };
};

type Props = {
  alert: SosAlert;
  rangeStart?: Date;
  rangeEnd?: Date;
};

const SosAlertDetailContent = ({ alert, rangeStart, rangeEnd }: Props) => {
  const formatSosTimestamp = useSosTimestampFormat();
  const timezone = alert.visit?.addressTimezone ?? alert.addressTimezone ?? undefined;
  const createdAt = useMemo(() => new Date(alert.createdAt), [alert.createdAt]);
  const range = useMemo(() => sosAlertRouteRange(alert), [alert]);
  const address = visitAddress(alert);
  const alertAddress = sosAlertAddress(alert);
  const patient = patientName(alert);

  const formatTimestamp = (value: string | Date) => formatSosTimestamp(value, timezone);

  const sosMarkers = useMemo<MapMarkerInput[]>(() => {
    if (!alert.latitude || !alert.longitude) {
      return [];
    }
    const point = coordinateToMapPoint(`${alert.latitude},${alert.longitude}`);
    if (!point) {
      return [];
    }
    return [
      {
        label: "SOS location",
        point,
        timestamp: createdAt,
        highlighted: true,
        icon: "person",
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.latitude, alert.longitude]);

  return (
    <Stack spacing={1.5}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {providerName(alert)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {`Reported · ${formatTimestamp(createdAt)}`}
      </Typography>
      {alert.providerStatus || patient ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {alert.providerStatus ? <VisitStatusChip status={alert.providerStatus} /> : null}
          {patient ? (
            <Typography variant="body2" color="text.secondary">
              {"Patient · "}
              <Link to={`/patients/${alert.visit?.patient?.id}`}>{patient}</Link>
              {address ? ` · ${address}` : null}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      {alertAddress || (alert.latitude && alert.longitude) ? (
        <Typography variant="body2" color="text.secondary">
          {[
            alertAddress,
            alert.latitude && alert.longitude
              ? `${alert.latitude}, ${alert.longitude}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {alert.provider ? (
            <RouteIndexView
              providerId={alert.provider.id}
              providerName={providerName(alert)}
              date={createdAt}
              compact
              rangeStart={rangeStart ?? range.rangeStart}
              rangeEnd={rangeEnd ?? range.rangeEnd}
              localVisitResources={EMPTY_VISIT_RESOURCES}
              localCalendarBlocks={EMPTY_CALENDAR_BLOCKS}
              localProviderContext={NULL_PROVIDER_CONTEXT}
              extraMarkers={sosMarkers}
              trackingMarkerIcon="person"
              trackStartPoint={sosMarkers[0]?.point}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No provider location available
            </Typography>
          )}
        </Box>
        {alert.events.length > 0 ? (
          <Box
            sx={{
              width: 340,
              flexShrink: 0,
              maxHeight: "57vh",
              overflowY: "auto",
              borderLeft: 1,
              borderColor: "divider",
              pl: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Event history
            </Typography>
            <Stack spacing={1.5}>
              {[...alert.events]
                .sort(
                  (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
                )
                .map((event) => (
                  <Stack key={event.id} direction="row" spacing={1.5}>
                    <Box
                      sx={{
                        // Match the first text line's height so the bullet
                        // centers on it regardless of theme line height.
                        height: (theme) =>
                          `calc(${theme.typography.body2.fontSize} * ${theme.typography.body2.lineHeight})`,
                        display: "flex",
                        alignItems: "center",
                        alignSelf: "flex-start",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "text.disabled",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2">
                        {EVENT_LABEL[event.eventType]}
                        {" · "}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {eventActor(event)}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(event.createdAt)}
                      </Typography>
                      {event.note ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {event.note}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                ))}
            </Stack>
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
};

export default SosAlertDetailContent;
